'use client';

import { Box } from '@terra/ui-styled-system';
import { useEffect, useState, type ReactNode } from 'react';
import { MetaDefault } from '../../../../../components/typography';
import { hasRenderableNode } from '../../chat-renderability';
import {
  TraceCollapsible,
  TraceCollapsibleContent,
  TraceCollapsibleTrigger,
  TraceLine,
} from '../../trace';

type InlineExecutionToolCardProps = {
  title: string;
  icon?: ReactNode;
  status?: string;
  error?: string;
  headerMeta?: ReactNode;
  headerActions?: ReactNode;
  children?: ReactNode;
  defaultExpanded?: boolean;
  autoCollapseWhenComplete?: boolean;
  forceExpanded?: boolean;
};

export function InlineExecutionToolCard({
  title,
  status,
  error,
  headerMeta,
  headerActions,
  children,
  defaultExpanded = false,
  forceExpanded,
}: InlineExecutionToolCardProps) {
  const isStarted = status === 'started' || status === 'in-progress';
  const hasRenderableChildren = hasRenderableNode(children);
  const canExpand = Boolean(hasRenderableChildren || error);
  const [isExpanded, setIsExpanded] = useState(
    defaultExpanded || forceExpanded === true || status === 'error',
  );
  const effectiveExpanded = forceExpanded || isExpanded;

  useEffect(() => {
    if (forceExpanded) {
      setIsExpanded(true);
    }
  }, [forceExpanded]);

  return (
    <TraceCollapsible
      open={effectiveExpanded}
      onOpenChange={({ open }) => {
        if (forceExpanded) {
          setIsExpanded(true);

          return;
        }

        if (canExpand) {
          setIsExpanded(open);
        }
      }}
      disabled={!canExpand}
    >
      <TraceCollapsibleTrigger disabled={!canExpand}>
        <TraceLine
          title={title}
          canExpand={canExpand}
          isExpanded={effectiveExpanded}
          status={
            status === 'error' ? 'error' : isStarted ? 'running' : undefined
          }
          trailing={
            <>
              {headerMeta}
              {headerActions}
            </>
          }
        />
      </TraceCollapsibleTrigger>
      <TraceCollapsibleContent open={canExpand && effectiveExpanded}>
        <Box w="full" pt={1}>
          {error ? (
            <MetaDefault color="fg.error" whiteSpace="pre-wrap">
              {error}
            </MetaDefault>
          ) : null}
          {hasRenderableChildren ? children : null}
        </Box>
      </TraceCollapsibleContent>
    </TraceCollapsible>
  );
}
